import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  getExerciseRecommendations,
  assignExercise,
  unassignExercise,
} from "../../../redux/actions/exerciseRecommendations.js";

import arrowRightIcon from "../../../assets/images/arrow-right.svg";

const SciRecommendations = ({ patientId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [selectedRecommendation, setSelectedRecommendation] = useState(null);

  const recommendations = useSelector(
    (state) => state.exerciseRecommendations?.recommendations || []
  );

  const groupedRecommendations = useMemo(() => {
    if (!Array.isArray(recommendations) || recommendations.length === 0)
      return {};

    return recommendations.reduce((acc, exercise) => {
      if (!acc[exercise.category]) acc[exercise.category] = [];
      acc[exercise.category].push(exercise);
      return acc;
    }, {});
  }, [recommendations]);

  const getAssignedCount = (groupedExercises) => {
    if (!Array.isArray(groupedExercises)) {
      return 0;
    }

    return groupedExercises.reduce((count, item) => {
      if (item.is_assigned !== undefined) {
        return count + (item.is_assigned ? 1 : 0);
      }

      if (Array.isArray(item.exercises)) {
        return (
          count +
          item.exercises.filter((exercise) => exercise.is_assigned).length
        );
      }

      return count;
    }, 0);
  };

  useEffect(() => {
    if (patientId) {
      dispatch(getExerciseRecommendations(patientId));
    }
  }, [dispatch, patientId]);

  const handleAssignAction = (exerciseId, isAssigned) => {
    if (isAssigned) {
      dispatch(unassignExercise(exerciseId, patientId));
    } else {
      dispatch(assignExercise(exerciseId, patientId));
    }
  };

  const RecommendationDetailsModal = ({ exercises }) => {
    return (
      <div className="recommendation-details-modal">
        <div className="modal-body">
          {exercises ? (
            exercises.map((exercise, index) => (
              <div
                className={`exercise-row ${
                  index % 2 === 0 ? "even-row" : "odd-row"
                }`}
                key={index}
              >
                <div className='exercise-name-wrapper'>
                  <span className='exercise-name'>{exercise.name}</span>
                  {!exercise.is_assigned && (
                    <span className='alert-badge'>!</span>
                  )}
                </div>
                <button
                  onClick={() =>
                    handleAssignAction(exercise.id, exercise.is_assigned)
                  }
                  className={
                    exercise.is_assigned
                      ? "badge-prescribed"
                      : "prescribe-button"
                  }
                >
                  {t(
                    exercise.is_assigned
                      ? "home.sci_recommendations.assigned"
                      : "home.sci_recommendations.assign"
                  )}
                </button>
              </div>
            ))
          ) : (
            <p className='no-exercises-text'>
              {t("home.sci_recommendations.no_exercises")}
            </p>
          )}
        </div>
        <div className="modal-footer">
          <span>
            {t("home.sci_recommendations.prescribed_exercises")}
            {getAssignedCount(exercises)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className='data-card'>
      <div className='card-title'>
        {selectedRecommendation === null ? (
          <span className='small-title'>
            {t("home.sci_recommendations.card_title")}
          </span>
        ) : (
          <div className='modal-header'>
            <button
              className='back-button'
              onClick={() => setSelectedRecommendation(null)}
            >
              &lt; {selectedRecommendation?.[0].category}
            </button>
          </div>
        )}
      </div>
      {selectedRecommendation ? (
        <RecommendationDetailsModal
          exercises={selectedRecommendation}
          onClose={() => setSelectedRecommendation(null)}
        />
      ) : Object.keys(groupedRecommendations).length === 0 ? (
        <p className="no-data-text">{t("home.sci_recommendations.no_data")}</p>
      ) : (
        <div className="row bg-danger">
          {Object.entries(groupedRecommendations).map(
            ([category, exercises], index) => (
              <div className="col-6" key={index}>
                <div
                  className="recommendation-card"
                  onClick={() => setSelectedRecommendation(exercises)}
                >
                  <div className="d-flex-space-between">
                    <h5 className="recommendation-title">{category}</h5>
                    <img
                      className="image-cursor"
                      src={arrowRightIcon}
                      alt="More"
                    />
                  </div>
                  {exercises.length !== getAssignedCount(exercises) && (
                    <div className="badge-primary">
                      <span className="badge-primary-text">
                        {exercises.length - getAssignedCount(exercises)}
                        {t(
                          "home.sci_recommendations.new_recommendation_number"
                        )}
                      </span>
                    </div>
                  )}
                  <div className='card-text-badge'>
                    <p className='card-text'>
                      {t("home.sci_recommendations.prescribed_badge")}
                    </p>
                    <span
                      className={`exercise-badge ${
                        getAssignedCount(exercises) > 0
                          ? "exercise-badge-green"
                          : ""
                      }`}
                    >
                      {getAssignedCount(exercises)}
                    </span>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default SciRecommendations;
